import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { ArrowUp, ArrowDown } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TickerItem({ item, prevPrice }) {
  const changed = prevPrice !== undefined && prevPrice !== item.price;
  const up = item.change >= 0;

  return (
    <div className="inline-flex items-center gap-2 px-5 shrink-0">
      <span className="text-[11px] font-mono text-zinc-500 font-medium">{item.name}</span>
      <span
        className={`text-[11px] font-mono tabular-nums font-medium transition-colors duration-300 ${
          changed ? (up ? "text-green-600" : "text-red-600") : "text-zinc-800"
        }`}
      >
        {item.price?.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </span>
      <span
        className={`text-[11px] font-mono tabular-nums flex items-center gap-0.5 ${
          up ? "text-green-600" : "text-red-600"
        }`}
      >
        {up ? <ArrowUp size={9} weight="bold" /> : <ArrowDown size={9} weight="bold" />}
        {up ? "+" : ""}
        {item.change_percent?.toFixed(2)}%
      </span>
    </div>
  );
}

export default function MarketBar() {
  const [indices, setIndices] = useState([]);
  const [prevPrices, setPrevPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/market/indices`);
        if (res.data?.length > 0) {
          setPrevPrices((prev) => {
            const map = {};
            indices.forEach((i) => { map[i.symbol] = i.price; });
            return map;
          });
          setIndices(res.data);
        }
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    fetchData();
    const id = setInterval(fetchData, 30000);
    return () => clearInterval(id);
  }, []);

  if (loading || indices.length === 0) {
    return (
      <div className="w-full border-b border-zinc-800 bg-zinc-950 sticky top-0 z-40 overflow-hidden" data-testid="market-bar">
        <div className="relative h-9 flex items-center">
          <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-zinc-950 via-zinc-950 to-transparent z-10 flex items-center pl-3">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
          </div>
          <div className="flex items-center gap-8 pl-10">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="inline-flex items-center gap-2 shrink-0">
                <div className="h-2.5 w-12 bg-zinc-800 rounded-sm animate-pulse" />
                <div className="h-2.5 w-16 bg-zinc-800/60 rounded-sm animate-pulse" style={{ animationDelay: `${i * 100}ms` }} />
              </div>
            ))}
          </div>
          <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-zinc-950 to-transparent z-10" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full border-b border-zinc-800 bg-zinc-950 sticky top-0 z-40 overflow-hidden"
      data-testid="market-bar"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-9 flex items-center">
        {/* Live indicator */}
        <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-zinc-950 via-zinc-950 to-transparent z-10 flex items-center pl-3">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>
        </div>

        {/* Scrolling ticker */}
        <div
          ref={scrollRef}
          className="flex items-center ticker-scroll"
          style={{
            animationPlayState: paused ? "paused" : "running",
          }}
        >
          {/* Duplicate for seamless loop */}
          {[...indices, ...indices].map((item, i) => (
            <TickerItem
              key={`${item.symbol}-${i}`}
              item={item}
              prevPrice={prevPrices[item.symbol]}
            />
          ))}
        </div>

        {/* Right fade */}
        <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-zinc-950 to-transparent z-10" />
      </div>
    </div>
  );
}
